import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Box, IconButton, styled, Typography, Skeleton } from '@mui/material';
import { Play, Pause, SpeakerSlash } from '@phosphor-icons/react';
import WaveSurfer from 'wavesurfer.js';

const WaveformContainer = styled(Box)({
  width: '100%',
  backgroundColor: '#FFF',
});

const PlayPauseButton = styled(IconButton)(({ theme }) => ({
  width: '36px',
  height: '36px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '50%',
  color: '#FFF',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '& svg': {
    width: '20px',
    height: '20px',
  },
}));

const NoRecordingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  backgroundColor: '#F9FAFB',
  borderRadius: '8px',
});

interface WaveSurferProps {
  audioUrl?: string;
  isLoading?: boolean;
}

const WaveSurferComponent: React.FC<WaveSurferProps> = memo(
  ({ audioUrl, isLoading }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState<string | null>(null);
    const [waveformReady, setWaveformReady] = useState(false);
    const [, setCurrentTime] = useState(0);

    useEffect(() => {
      // Reset states when audioUrl changes
      setAudioLoading(true);
      setAudioError(null);
      setIsPlaying(false);
      setWaveformReady(false);

      if (!containerRef.current || !audioUrl) {
        setAudioLoading(false);
        return;
      }

      // Destroy previous instance if it exists
      if (wavesurferRef.current) {
        wavesurferRef.current.unAll();
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }

      const wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: 'rgba(71, 37, 223, 0.3)',
        progressColor: 'rgba(52, 11, 236, 0.9)',
        cursorWidth: 1,
        barWidth: 2,
        barGap: 3,
        barRadius: 3,
        height: 48,
        normalize: true,
        interact: true,
      });

      // Event listeners for loading states
      //
      wavesurfer.on('loading', progress => {
        console.log('Loading audio: ' + progress + '%');
      });
      // Use decode event instead of ready - this fires when waveform is ready to display
      wavesurfer.on('decode', () => {
        setAudioLoading(false);
        setWaveformReady(true);
      });

      // Also listen to ready event as a fallback
      wavesurfer.on('ready', () => {
        setAudioLoading(false);
        setWaveformReady(true);
      });

      wavesurfer.on('error', err => {
        const errorMessage =
          typeof err === 'string' ? err : 'Failed to load audio.';
        setAudioError(errorMessage);
        setAudioLoading(false);
        setWaveformReady(false);
        console.error('Wavesurfer error:', err);
      });

      wavesurfer.on('play', () => setIsPlaying(true));
      wavesurfer.on('pause', () => setIsPlaying(false));
      wavesurfer.on('finish', () => setIsPlaying(false));
      wavesurfer.on('audioprocess', () => {
        setCurrentTime(wavesurfer.getCurrentTime());
      });

      wavesurferRef.current = wavesurfer;
      wavesurfer.load(audioUrl);

      return () => {
        if (wavesurferRef.current) {
          wavesurferRef.current.unAll();
          wavesurferRef.current.destroy();
        }
      };
    }, [audioUrl]);

    const handlePlayPause = useCallback(() => {
      if (wavesurferRef.current && waveformReady) {
        wavesurferRef.current.playPause();
      }
    }, [waveformReady]);

    // Show error state
    if (audioError) {
      return (
        <WaveformContainer>
          <NoRecordingContainer>
            <SpeakerSlash size={24} weight="fill" color="#EF4444" />
            <Typography sx={{ color: '#EF4444', fontSize: '14px' }}>
              Error: {audioError}
            </Typography>
          </NoRecordingContainer>
        </WaveformContainer>
      );
    }

    // Show no recording message
    if (!audioUrl) {
      return (
        <WaveformContainer>
          <NoRecordingContainer>
            <SpeakerSlash size={24} weight="fill" color="#6B7280" />
            <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
              No recording was found for this call
            </Typography>
          </NoRecordingContainer>
        </WaveformContainer>
      );
    }

    // Always show the waveform container, but overlay skeleton when loading
    return (
      <WaveformContainer>
        <Box
          sx={{
            px: 2,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            position: 'relative',
          }}
        >
          <PlayPauseButton onClick={handlePlayPause} disabled={!waveformReady}>
            {isPlaying ? <Pause weight="fill" /> : <Play weight="fill" />}
          </PlayPauseButton>

          <Box sx={{ flex: 1, position: 'relative' }}>
            {/* Waveform container - always visible */}
            <Box ref={containerRef} />

            {/* Skeleton overlay - only visible when loading */}
            {(isLoading || audioLoading) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#FFF',
                  zIndex: 1,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={48}
                  sx={{ borderRadius: 1 }}
                  animation="wave"
                />
              </Box>
            )}
          </Box>
        </Box>
      </WaveformContainer>
    );
  }
);

export default memo(WaveSurferComponent);
